export type OntologyJsonValueDto =
  | null
  | boolean
  | number
  | string
  | OntologyJsonValueDto[]
  | { [key: string]: OntologyJsonValueDto };

export type OntologyJsonObjectDto = Record<string, OntologyJsonValueDto>;

export const ONTOLOGY_OBJECT_KINDS = [
  'PERSON',
  'POSITION',
  'ORG_UNIT',
  'OBJECTIVE',
  'CAPABILITY',
  'SKILL_EVIDENCE',
  'COST',
  'ALLOCATION',
  'INITIATIVE',
  'WORK_ITEM',
  'POLICY_RULE',
  'CONSTRAINT',
  'METRIC',
  'EVIDENCE',
  'ASSUMPTION',
  'DECISION',
  'INTERVENTION',
  'OWNER',
  'REVIEW_CADENCE',
  'MONITORING_SIGNAL',
  'DRIFT_EVENT',
] as const;

export type OntologyObjectKindDto = (typeof ONTOLOGY_OBJECT_KINDS)[number];

export const ONTOLOGY_RELATIONSHIP_KINDS = [
  'REPORTS_TO',
  'MEMBER_OF',
  'HAS_COST',
  'HAS_EVIDENCE_FOR',
  'REQUIRES',
  'ALLOCATED_TO',
  'WORKS_ON',
  'ADVANCES',
  'CONSTRAINS',
  'DEPENDS_ON',
  'CONSTRAINED_BY',
  'MEASURES',
  'SUPPORTS',
  'ASSUMES',
  'APPROVES',
  'MOVES',
  'ACCOUNTABLE_FOR',
  'REVIEWED_ON',
  'DETECTS_DRIFT_AGAINST',
  'AFFECTS',
  'BLOCKS',
  'REFERENCES',
] as const;

export type OntologyRelationshipKindDto = (typeof ONTOLOGY_RELATIONSHIP_KINDS)[number];

export const ONTOLOGY_ACTION_KINDS = [
  'APPROVE_INTERVENTION',
  'ASSIGN_OWNER',
  'REALLOCATE_CAPACITY',
  'DEFER_DECISION',
  'ESCALATE_GAP',
  'ATTACH_EVIDENCE',
  'MARK_ASSUMPTION',
  'SET_REVIEW_CADENCE',
  'START_MONITORING',
  'EMIT_DRIFT_EVENT',
] as const;

export type OntologyActionKindDto = (typeof ONTOLOGY_ACTION_KINDS)[number];

export const ONTOLOGY_EVENT_KINDS = [
  'OBJECT_CREATED',
  'OBJECT_UPDATED',
  'OBJECT_SUPERSEDED',
  'OBJECT_ARCHIVED',
  'RELATIONSHIP_CREATED',
  'RELATIONSHIP_ARCHIVED',
  'EVIDENCE_ATTACHED',
  'ACTION_SUBMITTED',
  'ACTION_COMPLETED',
  'ACTION_BLOCKED',
  'SNAPSHOT_CREATED',
] as const;

export type OntologyEventKindDto = (typeof ONTOLOGY_EVENT_KINDS)[number];

export type OntologyStatusDto = 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'ARCHIVED';

export type OntologyActionStatusDto = 'PROPOSED' | 'SUBMITTED' | 'BLOCKED' | 'COMPLETED' | 'FAILED';

export type OntologyProofLevelDto =
  | 'verified'
  | 'source_linked'
  | 'design_stage'
  | 'assumption'
  | 'heuristic'
  | 'conflict'
  | 'gap'
  | 'unknown'
  | 'refused';

export type OntologySensitivityDto =
  | 'NONE'
  | 'PEOPLE_PII'
  | 'PEOPLE_HIGHLY_SENSITIVE'
  | 'FINANCE'
  | 'LEGAL'
  | 'SECRET'
  | 'MIXED';

export const ONTOLOGY_AGENT_TOOL_RESULT_STATUSES = [
  'ok',
  'blocked',
  'not_found',
  'unauthorized',
  'gap',
] as const;

export type OntologyToolResultStatusDto = (typeof ONTOLOGY_AGENT_TOOL_RESULT_STATUSES)[number];

export type OntologyEndpoint = readonly [string, string, string];
export type MaOntologyInput = { name: string; source: string; required?: boolean;
  default?: null; description: string };
export type MaOntologyTopology = {
  types: { name: string; meaning: string; idPattern: string; sources: string[] }[];
  relations: { name: string; from: string | string[]; to: string | string[]; meaning?: string }[];
  version: string; asOfSemantics: string;
  levers: { name: string; label: string; reversible: boolean; reversibilityMeaning: string;
    inputs: MaOntologyInput[]; constraints: string[] }[];
};

/** Shape admission does not grant activation; preserve the separate synapse fence. */
export const ONTOLOGY_SYNAPSE_ENDPOINTS: readonly OntologyEndpoint[] = [
  ["METRIC","DEPENDS_ON","METRIC"],
  ["METRIC","MOVES","METRIC"],
  ["METRIC","REFERENCES","METRIC"],
  ["CAPABILITY","ACCOUNTABLE_FOR","METRIC"],
  ["CAPABILITY","MEASURES","METRIC"],
  ["CAPABILITY","MOVES","METRIC"],
  ["CAPABILITY","REQUIRES","EVIDENCE"],
  ["CAPABILITY","REFERENCES","CAPABILITY"],
  ["CAPABILITY","SUPPORTS","CAPABILITY"],
  ["OWNER","ACCOUNTABLE_FOR","CAPABILITY"],
];
export const ONTOLOGY_CORE_ENDPOINTS: readonly OntologyEndpoint[] = [
  ["PERSON","REPORTS_TO","PERSON"],
  ["PERSON","MEMBER_OF","ORG_UNIT"],
  ["POSITION","MEMBER_OF","ORG_UNIT"],
  ["POSITION","HAS_COST","COST"],
  ["PERSON","HAS_EVIDENCE_FOR","CAPABILITY"],
  ["SKILL_EVIDENCE","SUPPORTS","CAPABILITY"],
  ["POSITION","REQUIRES","CAPABILITY"],
  ["INITIATIVE","REQUIRES","CAPABILITY"],
  ["ALLOCATION","ALLOCATED_TO","INITIATIVE"],
  ["PERSON","WORKS_ON","WORK_ITEM"],
  ["WORK_ITEM","ADVANCES","INITIATIVE"],
  ["INITIATIVE","ADVANCES","OBJECTIVE"],
  ["POLICY_RULE","CONSTRAINS","INITIATIVE"],
  ["OBJECTIVE","DEPENDS_ON","CAPABILITY"],
  ["CAPABILITY","CONSTRAINED_BY","CONSTRAINT"],
  ["OBJECTIVE","CONSTRAINED_BY","CONSTRAINT"],
  ["METRIC","MEASURES","OBJECTIVE"],
  ["EVIDENCE","SUPPORTS","METRIC"],
  ["EVIDENCE","SUPPORTS","ASSUMPTION"],
  ["DECISION","APPROVES","INTERVENTION"],
  ["INTERVENTION","MOVES","METRIC"],
  ["OWNER","ACCOUNTABLE_FOR","INTERVENTION"],
  ["OWNER","ACCOUNTABLE_FOR","OBJECTIVE"],
  ["REVIEW_CADENCE","REVIEWED_ON","OBJECTIVE"],
  ["MONITORING_SIGNAL","DETECTS_DRIFT_AGAINST","DECISION"],
  ["DRIFT_EVENT","AFFECTS","OBJECTIVE"],
  ["DRIFT_EVENT","AFFECTS","METRIC"],
  ["OBJECTIVE","REFERENCES","DECISION"],
  ["INTERVENTION","BLOCKS","CONSTRAINT"],
  ["PERSON","ALLOCATED_TO","INITIATIVE"],
  ["POSITION","ALLOCATED_TO","INITIATIVE"],
  ["PERSON","HAS_COST","COST"],
  ["INITIATIVE","HAS_COST","COST"],
  ...ONTOLOGY_SYNAPSE_ENDPOINTS,
];

const RECOVERY_INPUTS: Record<string, MaOntologyInput> = {
  mappedSpend: {"name":"mappedSpend","source":"governed","required":true,"description":"Exact mapped recurring accounts, latest complete quarter; uncovered components excluded."},
  spendContinues: {"name":"spendContinues","source":"assumption","default":null,"description":"Historical spending continues at the quarter average."},
  mappedScopeRemovable: {"name":"mappedScopeRemovable","source":"assumption","default":null,"description":"Covered mapped scope is eligible for this intervention; duplicate signals alone do not establish this."},
  dependencyCleared: {"name":"dependencyCleared","source":"assumption","default":null,"description":"Required ERP and operational prerequisites will be cleared by effect date."},
  oneTimeCostUsd: {"name":"oneTimeCostUsd","source":"assumption","default":null,"description":"LOW/BASE/HIGH total fees, migration and implementation costs."},
  monthsToEffect: {"name":"monthsToEffect","source":"assumption","default":null,"description":"Whole future months before savings; renewal wait for renewal option."},
  renewalMonth: {"name":"renewalMonth","source":"assumption","default":null,"description":"LOW/BASE/HIGH YYYY-MM; alternative to monthsToEffect."},
  residualAnnualSpendUsd: {"name":"residualAnnualSpendUsd","source":"assumption","default":null,"description":"LOW/BASE/HIGH negotiated or buyer replacement annual spending; no assumed discount."},
};

const MA_TOPOLOGY: MaOntologyTopology = {
  types: [
    {"name":"Deal","meaning":"A governed acquisition and its promise set.","idPattern":"published deal_ref","sources":["phoenix_deal_commitment_lines","deal_case"]},
    {"name":"Commitment","meaning":"A governed delivery promise within a deal.","idPattern":"commitment:<published-ref>","sources":["phoenix_deal_commitment_lines"]},
    {"name":"Dependency","meaning":"An explicitly published prerequisite of a commitment.","idPattern":"dependency:<published-ref>","sources":["synergy_value_capture"]},
    {"name":"OwnerRole","meaning":"The published role accountable for delivery.","idPattern":"published accountable_seat_ref within deal","sources":["phoenix_commitment_bindings"]},
    {"name":"ThesisOutcome","meaning":"A published strategic outcome, distinct from a thesis pillar.","idPattern":"published thesis_ref within deal","sources":["phoenix_commitment_bindings"]},
    {"name":"MeasurementBinding","meaning":"A published computation binding, distinct from a measure.","idPattern":"published binding_ref within deal","sources":["phoenix_commitment_bindings"]},
    {"name":"Decision","meaning":"A recorded decision acting on a governed scope.","idPattern":"published ledger id","sources":["DecisionLedgerDecision"]},
    {"name":"Measure","meaning":"An engine-defined business quantity, never model arithmetic.","idPattern":"published manifest measure ref","sources":["governed dataset manifest measures"]},
    {"name":"Dataset","meaning":"A governed dataset that publishes measures and dimensions.","idPattern":"published dataset ref","sources":["governed dataset manifest"]},
    {"name":"ThesisPillar","meaning":"A published strategic pillar supported by delivery commitments.","idPattern":"pillar:<dealId>:<pillar_ref>","sources":["synergy_thesis"]},
    {"name":"Signal","meaning":"An observed delivery indicator or status association; not causal proof.","idPattern":"signal:<row_key> or associated_signal:<sha256(scope,signal)>","sources":["synergy_value_capture"]},
    {"name":"Milestone","meaning":"A governed delivery checkpoint.","idPattern":"published milestone ref","sources":["synergy_value_capture"]},
    {"name":"GLComponent","meaning":"A governed general-ledger account or subfunction linked by an authored crosswalk.","idPattern":"gl:account/<code> or gl:subfunction/<function>/<subfunction>","sources":["phoenix_it_gl_crosswalk","relationship_graph"]},
    {"name":"Lever","meaning":"An editable what-if intervention, never an approved action.","idPattern":"lever:<name>","sources":["ontology v1.1"]},
  ],
  relations: [
    {"name":"has_binding","from":"Commitment","to":"MeasurementBinding","meaning":"A commitment has this published computation binding."},
    {"name":"enabling_commitment","from":"ThesisOutcome","to":"Commitment","meaning":"A published commitment enables this strategic outcome."},
    {"name":"leading_indicator","from":"ThesisOutcome","to":"MeasurementBinding","meaning":"A published binding measures a leading indicator for this outcome."},
    {"name":"lagging_outcome","from":"ThesisOutcome","to":"MeasurementBinding","meaning":"A published binding measures a lagging result for this outcome."},
    {"name":"success_measurement","from":"ThesisOutcome","to":"MeasurementBinding","meaning":"A published binding measures success for this outcome."},
    {"name":"has_commitment","from":"Deal","to":"Commitment","meaning":"A deal includes a published delivery commitment."},
    {"name":"has_measure","from":"Dataset","to":"Measure","meaning":"A dataset declares this governed measure."},
    {"name":"depends_on","from":"Commitment","to":"Dependency","meaning":"A commitment has this explicitly published prerequisite."},
    {"name":"owned_by","from":["Commitment","Decision"],"to":"OwnerRole","meaning":"The published role is accountable for this entity."},
    {"name":"acts_on","from":"Decision","to":["Deal","Commitment","Dependency"],"meaning":"A recorded decision acts on this governed scope."},
    {"name":"measured_by","from":["Deal","Commitment"],"to":"Measure","meaning":"Published rows record this governed measure for the entity."},
    {"name":"has_pillar","from":"Deal","to":"ThesisPillar","meaning":"A deal includes this published strategic pillar."},
    {"name":"pillar_has_commitment","from":"ThesisPillar","to":"Commitment","meaning":"A delivery commitment supports this published strategic pillar."},
    {"name":"signals_on","from":"Signal","to":["Commitment","Dependency","Milestone"],"meaning":"A published delivery indicator observes this entity."},
    {"name":"draws_on","from":"Commitment","to":"GLComponent","meaning":"An authored crosswalk links this commitment to a ledger component."},
    {"name":"flagged_by_signal","from":["Commitment","Dependency"],"to":"Signal","meaning":"A governed delivery indicator flags this entity; association is not causal proof."},
    {"name":"addressed_by","from":"Signal","to":"Lever","meaning":"This editable intervention addresses the governed signal."},
    {"name":"published_in","from":"Commitment","to":"Dataset","meaning":"The dataset publishes this entity; sources describe dataset-level provenance."},
  ],
  version: "2.1.0",
  asOfSemantics: "Business effective date within the current published revision set; not a historical publication-time or ledger-status snapshot. Revision digests identify the source snapshot.",
  levers: [
    { ...{"name":"terminate_now","label":"Terminate now","reversible":false,"reversibilityMeaning":"Assume execution is not reversible.","constraints":["monthsToEffect must be zero"]},
      inputs: ["mappedSpend","spendContinues","mappedScopeRemovable","dependencyCleared","oneTimeCostUsd","monthsToEffect"].map(name => RECOVERY_INPUTS[name]) },
    { ...{"name":"terminate_at_renewal","label":"Terminate at renewal","reversible":true,"reversibilityMeaning":"Proposal can be withdrawn before signing; not a promise that an executed contract is reversible.","constraints":[]},
      inputs: ["mappedSpend","spendContinues","mappedScopeRemovable","dependencyCleared","oneTimeCostUsd","monthsToEffect","renewalMonth"].map(name => RECOVERY_INPUTS[name]) },
    { ...{"name":"consolidate_onto_buyer_contract","label":"Consolidate onto buyer contract","reversible":false,"reversibilityMeaning":"Assume execution is not reversible.","constraints":[]},
      inputs: ["mappedSpend","spendContinues","mappedScopeRemovable","dependencyCleared","oneTimeCostUsd","monthsToEffect","residualAnnualSpendUsd"].map(name => RECOVERY_INPUTS[name]) },
    { ...{"name":"renegotiate","label":"Renegotiate","reversible":true,"reversibilityMeaning":"Proposal can be withdrawn before signing; not a promise that an executed contract is reversible.","constraints":[]},
      inputs: ["mappedSpend","spendContinues","mappedScopeRemovable","dependencyCleared","oneTimeCostUsd","monthsToEffect","residualAnnualSpendUsd","renewalMonth"].map(name => RECOVERY_INPUTS[name]) },
  ],
};

/** Legacy non-model output descriptions; never included in the topology/model dictionary. */
const LEGACY_MA_MEASURES = [
  {"name":"target_run_rate_usd","meaning":"Underwritten annualized run-rate promised by the commitment."},
  {"name":"planned_value_usd","meaning":"Engine-recorded current planned annualized operating-income impact."},
  {"name":"actual_value_usd","meaning":"Observed annualized realized value at the reported as-of."},
  {"name":"plan_minus_target_usd","meaning":"Engine-computed change from underwritten value to current plan."},
  {"name":"shortfall_usd","meaning":"Engine-computed delivery shortfall against the plan at the same as-of."},
  {"name":"root_decision_committed_usd","meaning":"Value validated by approved decisions in the engine's governed scope."},
  {"name":"days_late","meaning":"Engine-computed schedule delay against the committed date."},
  {"name":"actual_count","meaning":"Observed completed units in the engine-declared unit and period."},
  {"name":"planned_count","meaning":"Planned units in the engine-declared unit and period."},
  {"name":"annualized_spend_usd","meaning":"Covered historical quarter spend multiplied by twelve divided by three; not a recovery promise."},
  {"name":"recoverable_value_usd","meaning":"Modeled gross recovery for the rest of the calendar year after asOf."},
  {"name":"recoverable_run_rate_usd","meaning":"Annual run-rate recovery"},
  {"name":"one_time_cost_usd","meaning":"Explicit scenario fees and implementation costs."},
  {"name":"net_recovery_usd","meaning":"Modeled rest-of-year gross recovery less one-time cost."},
  {"name":"recoverable_value_first_year_usd","meaning":"Modeled gross recovery for the first 12 whole calendar months after asOf."},
  {"name":"net_recovery_first_year_usd","meaning":"Modeled first-year gross recovery less the full one-time cost."},
  {"name":"months_to_effect","meaning":"Explicit whole-month scenario delay."},
];

/** One declaration owner; equal spellings in different namespaces confer no equivalence. */
export const ONTOLOGY_TOPOLOGY = {
  core: { objectKinds: ONTOLOGY_OBJECT_KINDS, relationshipKinds: ONTOLOGY_RELATIONSHIP_KINDS,
    endpoints: ONTOLOGY_CORE_ENDPOINTS,
    relationMeanings: {
      ADVANCES: 'An explicit published initiative binding advances this objective.',
      ALLOCATED_TO: 'A published position assignment links this position to the initiative; it is not people, FTE or allocated cost.',
      REPORTS_TO: 'The published reporting hierarchy identifies an ancestor; ancestry does not establish accountable ownership.',
      HAS_COST: 'A governed source declares the cost basis; annual position cost is not attributable initiative cost.',
      MEASURES: 'A published metric or observation measures the objective on its declared basis.',
      ACCOUNTABLE_FOR: 'A published person or role is explicitly accountable for this objective.',
    } },
  ma: MA_TOPOLOGY,
};

/** Generated compatibility snapshot at the existing harness JSON path must equal this view. */
export const MA_ONTOLOGY_COMPATIBILITY = {
  types: MA_TOPOLOGY.types, relations: MA_TOPOLOGY.relations, measures: LEGACY_MA_MEASURES,
  version: MA_TOPOLOGY.version, asOfSemantics: MA_TOPOLOGY.asOfSemantics, levers: MA_TOPOLOGY.levers,
};

export const ontologyRelationshipKey = (from: string, relation: string, to: string): string =>
  JSON.stringify([from, relation, to]);

export type OntologyMapIssue = { code: 'UNKNOWN_KIND' | 'UNKNOWN_RELATION' |
  'MISSING_ENDPOINT' | 'ILLEGAL_ENDPOINTS' | 'DUPLICATE_ID'; identity: string };
export type OntologyTopologyMap = {
  nodes: readonly { id: string; type: string }[];
  edges: readonly { from: string; relation: string; to: string }[];
};

/** Graph shape validation only; this checker never admits publication or activation. */
export function checkOntologyMap(namespace: 'core' | 'ma', map: OntologyTopologyMap): OntologyMapIssue[] {
  const kinds: readonly string[] = namespace === 'core' ? ONTOLOGY_OBJECT_KINDS
    : MA_TOPOLOGY.types.map(type => type.name);
  const relations: readonly string[] = namespace === 'core' ? ONTOLOGY_RELATIONSHIP_KINDS
    : MA_TOPOLOGY.relations.map(relation => relation.name);
  const endpoints = namespace === 'core' ? ONTOLOGY_CORE_ENDPOINTS : MA_TOPOLOGY.relations.flatMap(rule =>
    [rule.from].flat().flatMap(from => [rule.to].flat().map(to => [from, rule.name, to] as const)));
  const allowed = new Set(endpoints.map(([from, relation, to]) => ontologyRelationshipKey(from, relation, to)));
  const issues: OntologyMapIssue[] = [];
  const nodes = new Map<string, string>();
  for (const node of map.nodes) {
    if (nodes.has(node.id)) issues.push({ code: 'DUPLICATE_ID', identity: node.id });
    if (!kinds.includes(node.type)) issues.push({ code: 'UNKNOWN_KIND', identity: node.type });
    nodes.set(node.id, node.type);
  }
  for (const edge of map.edges) {
    const from = nodes.get(edge.from), to = nodes.get(edge.to);
    const identity = ontologyRelationshipKey(edge.from, edge.relation, edge.to);
    if (!relations.includes(edge.relation)) issues.push({ code: 'UNKNOWN_RELATION', identity });
    else if (!from || !to) issues.push({ code: 'MISSING_ENDPOINT', identity });
    else if (!allowed.has(ontologyRelationshipKey(from, edge.relation, to)))
      issues.push({ code: 'ILLEGAL_ENDPOINTS', identity });
  }
  return issues;
}

export type * from './ontology-contracts';
